import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Button,
    Space,
    Typography,
    Avatar,
    List,
    Tag,
    message,
    Spin,
    Alert,
    Checkbox
} from 'antd';
import { UserOutlined, ExclamationCircleOutlined, UnlockOutlined } from '@ant-design/icons';
import { useGetAdminUsersQuery, IApiUser } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { useImpersonation } from '../../hooks/useImpersonation';
import { Utils } from '../../utils/utils';
import { UserRoleEnum } from '../../enums/UserRoleEnum';

const { Text, Paragraph } = Typography;

interface ImpersonationModalProps {
    visible: boolean;
    onCancel: () => void;
    organization: {
        id: number;
        name: string;
        userCount: number;
    } | null;
}

export const ImpersonationModal: React.FC<ImpersonationModalProps> = ({
    visible,
    onCancel,
    organization
}) => {
    const { user } = useAuth();
    const { startImpersonation, isLoading: isImpersonating } = useImpersonation();
    const [form] = Form.useForm();
    const [selectedUser, setSelectedUser] = useState<IApiUser | null>(null);
    const [previewUnlocked, setPreviewUnlocked] = useState(false);
    
    // Check if current user is SuperAdmin
    const isSuperAdmin = Utils.userIsSuperAdmin(user);

    // Fetch organization users - now with proper server-side filtering
    const { data: usersData, isLoading: loadingUsers, error } = useGetAdminUsersQuery({
        jwtToken: user?.jwtToken || '',
        page: 1,
        limit: 100, // Get all users for the organization
        organisationId: organization?.id, // Filter by organization on the server
    }, {
        skip: !organization?.id || !user?.jwtToken, // Skip query if no organization or token
    });

    // Filter users based on current user's role and target user's role
    const organizationUsers = React.useMemo(() => {
        if (!usersData?.data) return [];
        return usersData.data.filter((u: IApiUser) => {
            // SUSPENDED users are impersonatable too (the API mints a JWT regardless) —
            // needed for concierge onboarding of invited users who haven't set a password yet.
            if (u.status !== 'ACTIVE' && u.status !== 'SUSPENDED') return false;

            // Nobody can impersonate SuperAdmins
            if (Utils.userIsSuperAdmin(u)) return false;
            
            // Regular Admins cannot impersonate other Admins
            return !(!isSuperAdmin && Utils.userIsAdmin(u));
            

        });
    }, [usersData, isSuperAdmin]);

    const handleUserSelect = (userId: number) => {
        const user = organizationUsers.find((u: IApiUser) => u.id === userId);
        setSelectedUser(user || null);
        form.setFieldValue('userId', userId);
    };

    const handleStartImpersonation = async (values: { userId: number }) => {
        if (!organization || !selectedUser) {
            message.error('Please select a user to impersonate');
            return;
        }

        try {
            // Use a default reason for both admin and super admin
            const reason = 'Admin support';
            await startImpersonation(
                organization.id,
                values.userId,
                reason,
                previewUnlocked ? 'PROFESSIONAL' : undefined,
            );
            onCancel(); // Close modal
            form.resetFields();
            setSelectedUser(null);
            setPreviewUnlocked(false);
        } catch (error) {
            // Error handling is done in the hook
        }
    };

    const handleCancel = () => {
        onCancel();
        form.resetFields();
        setSelectedUser(null);
        setPreviewUnlocked(false);
    };

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!visible) {
            setSelectedUser(null);
            form.resetFields();
            setPreviewUnlocked(false);
        }
    }, [visible, form]);

    return (
        <Modal
            title={
                <Space>
                    <UserOutlined />
                    <span>Impersonate User - {organization?.name}</span>
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            width={600}
            footer={null}
            destroyOnClose
        >
            <Alert
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                message="Admin Impersonation"
                description={
                    <div>
                        <Paragraph style={{ margin: '8px 0' }}>
                            You are about to impersonate a user account. This will:
                        </Paragraph>
                        <ul style={{ margin: '0 0 8px 16px', fontSize: '12px' }}>
                            <li>Log you in as the selected user with their exact permissions</li>
                            <li>Allow you to see the application from their perspective</li>
                            <li>Record this action in the audit log for security purposes</li>
                        </ul>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            This action will be recorded in the audit log for security purposes.
                        </Text>
                    </div>
                }
                style={{ marginBottom: 20 }}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleStartImpersonation}
                disabled={isImpersonating}
            >
                <Form.Item
                    label="Select User to Impersonate"
                    name="userId"
                    rules={[{ required: true, message: 'Please select a user to impersonate' }]}
                >
                    <div>
                        {loadingUsers ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <Spin size="small" />
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    Loading organization users...
                                </Text>
                            </div>
                        ) : error ? (
                            <Alert
                                type="error"
                                message="Failed to load users"
                                description="Unable to fetch organization users. Please try again."
                            />
                        ) : organizationUsers.length === 0 ? (
                            <Alert
                                type="info"
                                message="No users available"
                                description="No active users found in this organization that can be impersonated."
                            />
                        ) : (
                            <List
                                size="small"
                                dataSource={organizationUsers}
                                style={{ 
                                    maxHeight: '200px', 
                                    overflowY: 'auto',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '6px'
                                }}
                                renderItem={(orgUser: IApiUser) => (
                                    <List.Item
                                        key={orgUser.id}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: selectedUser?.id === orgUser.id ? '#fffbeb' : undefined,
                                            borderLeft: selectedUser?.id === orgUser.id ? '3px solid #F59E0B' : undefined,
                                        }}
                                        onClick={() => handleUserSelect(orgUser.id)}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <Avatar size="small" icon={<UserOutlined />} />
                                            }
                                            title={
                                                <Space>
                                                    <span>{orgUser.firstName} {orgUser.lastName}</span>
                                                    {orgUser.status === 'SUSPENDED' && (
                                                        <Tag color="orange">Not activated</Tag>
                                                    )}
                                                    {selectedUser?.id === orgUser.id && (
                                                        <Tag color="blue">Selected</Tag>
                                                    )}
                                                </Space>
                                            }
                                            description={
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        {orgUser.email}
                                                    </Text>
                                                    <br />
                                                    <Space size={4} style={{ marginTop: 4 }}>
                                                        {(Array.isArray(orgUser.roles) ? orgUser.roles : JSON.parse(orgUser.roles)).map((role: string) => (
                                                            <Tag key={role} color="geekblue">
                                                                {Utils.readableUserRole(role as UserRoleEnum)}
                                                            </Tag>
                                                        ))}
                                                    </Space>
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </div>
                </Form.Item>

                <Form.Item style={{ marginBottom: 12 }}>
                    <Checkbox
                        checked={previewUnlocked}
                        onChange={(e) => setPreviewUnlocked(e.target.checked)}
                    >
                        <Space size={6}>
                            <UnlockOutlined style={{ color: '#F59E0B' }} />
                            <span>Unlock Professional preview</span>
                        </Space>
                    </Checkbox>
                    <div style={{ marginLeft: 24 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            See the dashboard as if this organisation were on the Professional
                            plan — its real subscription is untouched, and only your session
                            sees the unlocked view. Leave off to see exactly what the user sees.
                        </Text>
                    </div>
                </Form.Item>

                <Form.Item>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCancel} disabled={isImpersonating}>
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={isImpersonating}
                            disabled={!selectedUser}
                        >
                            Start Impersonation
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};